import { useCallback } from 'react';
import {
  trackEvent,
  trackPageView,
  trackCustomEvent,
  trackBillView,
  trackLegislatureEmailAction,
  trackDonationClick,
  trackSocialShare,
  trackDarkModeToggle,
  trackLocationDetect,
  trackLegislatorMeetingSubmit,
  trackBlogArticleClick,
  trackOfficialBillLinkClick,
  trackJoinTeamSubmit,
} from '../components/GoogleAnalytics';

/**
 * Custom hook for Google Analytics tracking
 * Provides easy-to-use functions for tracking various user interactions
 */
export const useAnalytics = () => {
  const trackButtonClick = useCallback((buttonName: string, location: string) => {
    trackEvent('button_click', 'ui', `${buttonName} - ${location}`);
  }, []);

  const trackFormSubmission = useCallback((formName: string, success: boolean = true) => {
    trackEvent('form_submit', 'forms', formName, success ? 1 : 0);
  }, []);

  const trackDownload = useCallback((fileName: string, fileType: string) => {
    trackEvent('download', 'files', `${fileName}.${fileType}`);
  }, []);

  const trackExternalLink = useCallback((url: string, linkText?: string) => {
    trackEvent('external_link_click', 'navigation', linkText || url);
  }, []);

  const trackSearch = useCallback((searchTerm: string, resultCount?: number) => {
    trackEvent('search', 'search', searchTerm, resultCount);
  }, []);

  const trackTimeOnPage = useCallback((pageName: string, timeInSeconds: number) => {
    trackEvent('time_on_page', 'engagement', pageName, timeInSeconds);
  }, []);

  const trackError = useCallback((errorType: string, errorMessage: string) => {
    trackEvent('error', 'errors', `${errorType}: ${errorMessage}`);
  }, []);

  return {
    // Basic tracking
    trackEvent,
    trackPageView,
    trackCustomEvent,

    // App-specific tracking
    trackBillView,
    trackLegislatureEmailAction,
    trackDonationClick,
    trackSocialShare,
    trackDarkModeToggle,
    trackLocationDetect,
    trackLegislatorMeetingSubmit,
    trackBlogArticleClick,
    trackOfficialBillLinkClick,
    trackJoinTeamSubmit,

    // Generic UI tracking
    trackButtonClick,
    trackFormSubmission,
    trackDownload,
    trackExternalLink,
    trackSearch,
    trackTimeOnPage,
    trackError,
  };
};

export default useAnalytics;
